import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { X, Plus, Mail } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Collaborator {
  id: string;
  name: string;
  email: string;
  role: 'editor' | 'viewer' | 'owner';
  avatar?: string;
  active: boolean;
}

interface CollaboratorsListProps {
  collaborators: Collaborator[];
  onClose: () => void;
}

const CollaboratorsList: React.FC<CollaboratorsListProps> = ({ 
  collaborators,
  onClose 
}) => {
  const [newEmail, setNewEmail] = useState('');

  const inviteCollaborator = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a valid email address"
      });
      return;
    }

    // In a real app, this would send an invitation email
    toast({
      title: "Success",
      description: `Invitation sent to ${newEmail}`
    });
    setNewEmail('');
  };

  const changeRole = (collaboratorId: string, role: 'editor' | 'viewer' | 'owner') => {
    // In a real app, this would update the collaborator's role
    toast({
      title: "Role Updated",
      description: `Role updated to ${role}`
    });
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    if (collaborator.role === 'owner') {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You can't remove the document owner"
      });
      return;
    }
    
    // In a real app, this would remove the collaborator from the document
    toast({
      title: "Collaborator Removed",
      description: `${collaborator.name} removed from document`
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-md font-medium">Collaborators</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={inviteCollaborator} className="flex gap-2 mb-4">
          <Input
            placeholder="Email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="flex-grow"
          />
          <Button type="submit" size="sm" className="whitespace-nowrap">
            <Plus className="h-4 w-4 mr-1" />
            Invite
          </Button>
        </form>

        <div className="space-y-3">
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="flex items-center justify-between p-2 rounded-md border">
              <div className="flex items-center space-x-3">
                <div 
                  className={`h-8 w-8 rounded-full flex items-center justify-center text-primary-foreground text-sm relative ${
                    collaborator.active ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                >
                  {collaborator.name.charAt(0)}
                  {collaborator.active && (
                    <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 border border-white"></span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-sm">{collaborator.name}</p>
                  <p className="text-xs text-gray-500 flex items-center">
                    <Mail className="h-3 w-3 mr-1" />
                    {collaborator.email}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Select 
                  defaultValue={collaborator.role}
                  disabled={collaborator.role === 'owner'}
                  onValueChange={(value: 'editor' | 'viewer' | 'owner') => changeRole(collaborator.id, value)}
                >
                  <SelectTrigger className="w-24 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                
                {collaborator.role !== 'owner' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCollaborator(collaborator)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove</span>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 mt-4">
          Collaborators can view or edit this document depending on their role. Document owners can manage collaborators.
        </p>
      </CardContent>
    </Card>
  );
};

export default CollaboratorsList;
